"use client";

import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import Image from "next/image";

import { unstyledRichTextComponents } from "@up/ui";

import { QuoteMark } from "./QuoteMark";
import { StarRating } from "./StarRating";
import { VideoQuote } from "./VideoQuote";

import type { Content } from "@prismicio/client";

export type Testimonial2Item =
  Content.Testimonials2SliceDefaultPrimaryTestimonialsItem;

const AVATAR_SIZE = 96;

function videoTitle(item: Testimonial2Item) {
  return item.citation
    ? `Video testimonial from ${item.citation}`
    : "Video testimonial";
}

export function TestimonialCard({ item }: { item: Testimonial2Item }) {
  const hasVideo = isFilled.keyText(item.video_id);
  const hasQuote = isFilled.richText(item.quote);
  const rating = Number(item.rating);

  return (
    <figure className="testimonials2-item">
      {hasVideo ? (
        <VideoQuote
          id={item.video_id}
          orientation={item.video_orientation}
          source={item.video_source}
          title={videoTitle(item)}
        />
      ) : null}

      {!hasVideo && isFilled.image(item.photo) ? (
        <Image
          alt={item.photo.alt ?? ""}
          className="testimonials2-avatar"
          height={AVATAR_SIZE}
          loading="lazy"
          src={item.photo.url}
          width={AVATAR_SIZE}
        />
      ) : null}

      {hasQuote ? (
        <blockquote
          className={`testimonials2-quote testimonials2-quote--${
            item.quote_size ?? "medium"
          } rte`}
        >
          {/* Beside a video the graphic reads as clutter, not as a pull quote. */}
          {hasVideo ? null : <QuoteMark />}
          <div className="testimonials2-quote-body">
            <PrismicRichText
              components={unstyledRichTextComponents()}
              field={item.quote}
            />
          </div>
        </blockquote>
      ) : null}

      {rating || item.citation || item.citation_detail ? (
        <figcaption className="testimonials2-attribution">
          {rating ? <StarRating rating={rating} /> : null}
          {item.citation ? (
            <cite className="testimonials2-citation">{item.citation}</cite>
          ) : null}
          {item.citation_detail ? (
            <span className="testimonials2-citation-detail">
              {item.citation_detail}
            </span>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}
